import {
  usePaymentExport,
  usePaymentMethod,
  usePaymentPayTypeChoices,
  usePaymentReceivedStatusChoices,
  usePayments,
} from "@features/cx/payments/hooks";
import {
  CreditNote,
  PaymentDetail,
  PaymentFilters,
} from "@features/cx/payments/types";
import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Clock, DollarSign, FilterX } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaymentDetailsModal } from "../../Detail/PaymentDetail";
import { PaymentReceipt } from "../../Detail/PaymentReceipt";
import { CreditNoteDialog } from "../../creditNote/creditnotecreate";
import { TransferPaymentDialog } from "../transferPayment/transferPayment";
import { RefundPaymentDialog } from "../refundPayment/RefundPaymentDialog";

const statusBadge = (status: string | undefined) => {
  if (!status)
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
        Unknown
      </span>
    );
  if (status === "Active")
    return (
      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
        Active
      </span>
    );
  if (status === "Inactive")
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
        Inactive
      </span>
    );
  if (status === "Pending")
    return (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
        Pending
      </span>
    );
  if (status === "Disconnected")
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
        Disconnected
      </span>
    );
  return (
    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
      {status}
    </span>
  );
};

const paymentBadge = (status: string | undefined) => {
  if (!status)
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
        Unknown
      </span>
    );
  if (status === "Paid")
    return (
      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
        Paid
      </span>
    );
  if (status === "Overdue")
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
        Overdue
      </span>
    );
  if (status === "Pending")
    return (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
        Pending
      </span>
    );
  return (
    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
      {status}
    </span>
  );
};

export const PaymentList: React.FC = () => {
  const currency = useCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDetail | null>(
    null,
  );
  const [isPaymentDetailOpen, setIsPaymentDetailOpen] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isCreditNoteOpen, setIsCreditNoteOpen] = useState(false);
  const [isTransferPaymentOpen, setIsTransferPaymentOpen] = useState(false);
  const [isRefundPaymentOpen, setIsRefundPaymentOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  // const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE;
  const paymentMethodQuery = usePaymentMethod({
    remoteUtilityId: Number(remoteUtilityId) || 0,
  });

  const handleOpenModal = () => {
    setIsCreditNoteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreditNoteModalOpen(false);
  };
  const columns: TableColumn<CreditNote>[] = [
    { key: "receiptNo", header: "Receipt", type: "text", sortable: true },
    {
      key: "name",
      header: "Consumer",
      type: "custom",
      render: (_: any, row: CreditNote) => (
        <div>
          <div className="font-semibold">
            {row.consumerFirstName + " " + row.consumerLastName || "N/A"}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.consumerNo || "N/A"}
          </div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      type: "text",
      render: (value) => `${currency} ${value}`,
    },
    { key: "paymentModeDisplay", header: "Method", type: "text" },
    {
      key: "paymentDate",
      header: "Date",
      type: "custom",
      render: (value: string | undefined) => (
        <span className="flex items-center gap-1">
          <Clock className="h-4 w-4 text-gray-400" />
          {value || "N/A"}
        </span>
      ),
    },
    {
      key: "paymentPayType",
      header: "Payment Type",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === "Pending Approval") return "secondary";
          if (value === "Scheduled") return "outline";
          if (value === "In Progress") return "default";
          return "default";
        },
      },
    },
    {
      key: "paymentReceivedStatus",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === "Pending Approval") return "secondary";
          if (value === "Scheduled") return "outline";
          if (value === "In Progress") return "default";
          return "default";
        },
      },
    },
  ];
  const paymentPayTypeChoicesQuery = usePaymentPayTypeChoices(
    Number(remoteUtilityId) || 0,
  );
  const currentPage = parseInt(searchParams.get("page") || "1");

  const paymentReceivedStatusChoicesQuery = usePaymentReceivedStatusChoices(
    Number(remoteUtilityId) || 0,
  );

  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStartDate = searchParams.get("startDate") || "";
  const currentEndDate = searchParams.get("endDate") || "";
  const currentPaymentMethod = searchParams.get("paymentMode") || "all";
  const currentPaymentReceivedStatus =
    searchParams.get("paymentReceivedStatus") || "all";
  const currentPaymentPayType = searchParams.get("paymentPayType") || "all";

  // UI State for pending edits
  const [pendingFilters, setPendingFilters] = useState({
    startDate: currentStartDate,
    endDate: currentEndDate,
    paymentMethod: decodeURIComponent(currentPaymentMethod),
    paymentReceivedStatus: decodeURIComponent(currentPaymentReceivedStatus),
    paymentPayType: decodeURIComponent(currentPaymentPayType),
  });

  // Sync pendingFilters when URL changes
  useEffect(() => {
    setPendingFilters({
      startDate: currentStartDate,
      endDate: currentEndDate,
      paymentMethod: decodeURIComponent(currentPaymentMethod),
      paymentReceivedStatus: decodeURIComponent(currentPaymentReceivedStatus),
      paymentPayType: decodeURIComponent(currentPaymentPayType),
    });
  }, [
    currentStartDate,
    currentEndDate,
    currentPaymentMethod,
    currentPaymentReceivedStatus,
    currentPaymentPayType,
  ]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
      startDate: currentStartDate,
      endDate: currentEndDate,
      paymentMode: currentPaymentMethod,
      paymentReceivedStatus: currentPaymentReceivedStatus,
      paymentPayType: currentPaymentPayType,
    }),
    [
      currentPage,
      currentSearch,
      currentStartDate,
      currentEndDate,
      currentPaymentMethod,
      currentPaymentReceivedStatus,
      currentPaymentPayType,
    ],
  );

  const filters: PaymentFilters = useMemo(() => {
    const baseFilters: any = {
      page: queryParams.page,
      limit: PAGE_SIZE,
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
    };

    // Add date range filter using start and end dates
    if (queryParams.startDate) {
      baseFilters.startDate = queryParams.startDate; // Changed from dateFrom to startDate
    }

    if (queryParams.endDate) {
      baseFilters.endDate = queryParams.endDate; // Changed from dateTo to endDate
    }

    // Add payment method filter
    if (queryParams.paymentMode !== "all") {
      baseFilters.paymentMode = queryParams.paymentMode;
    }

    // Add payment received status filter
    if (queryParams.paymentReceivedStatus !== "all") {
      baseFilters.paymentReceivedStatus = queryParams.paymentReceivedStatus;
    }

    // Add payment pay type filter - Using keys directly
    if (queryParams.paymentPayType !== "all") {
      const paymentPayTypeKey = Number(queryParams.paymentPayType);
      baseFilters.paymentPayType = [paymentPayTypeKey];
    } else {
      // When "all" is selected, include all payment type keys
      baseFilters.paymentPayType = [2, 5, 6, 4, 10];
    }

    return baseFilters;
  }, [queryParams, remoteUtilityId]);

  // Export filters for payments (without pagination)
  const exportFilters: PaymentFilters = useMemo(() => {
    const baseFilters: any = {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
    };

    // Add date range filter using start and end dates
    if (queryParams.startDate) {
      baseFilters.startDate = queryParams.startDate;
    }

    if (queryParams.endDate) {
      baseFilters.endDate = queryParams.endDate;
    }

    // Add payment method filter
    if (queryParams.paymentMode !== "all") {
      baseFilters.paymentMode = queryParams.paymentMode;
    }

    // Add payment received status filter
    if (queryParams.paymentReceivedStatus !== "all") {
      baseFilters.paymentReceivedStatus = queryParams.paymentReceivedStatus;
    }

    // Add payment pay type filter - Using keys directly
    if (queryParams.paymentPayType !== "all") {
      const paymentPayTypeKey = Number(queryParams.paymentPayType);
      baseFilters.paymentPayType = [paymentPayTypeKey];
    } else {
      // When "all" is selected, include all payment type keys
      baseFilters.paymentPayType = [2, 5, 6];
    }

    return baseFilters;
  }, [queryParams, remoteUtilityId]);

  const { data } = usePayments(filters);
  const { exportCSV, isExporting } = usePaymentExport(exportFilters);

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string,
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    console.log("pendingFilters", pendingFilters);
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.startDate && { startDate: pendingFilters.startDate }),
      ...(pendingFilters.endDate && { endDate: pendingFilters.endDate }),
      ...(pendingFilters.paymentMethod && {
        paymentMode: pendingFilters.paymentMethod,
      }),
      ...(pendingFilters.paymentReceivedStatus && {
        paymentReceivedStatus: pendingFilters.paymentReceivedStatus,
      }),
      ...(pendingFilters.paymentPayType && {
        paymentPayType: pendingFilters.paymentPayType,
      }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.startDate;
    delete newParams.endDate;
    delete newParams.paymentMode;
    delete newParams.paymentReceivedStatus;
    delete newParams.paymentPayType;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentStartDate && { startDate: currentStartDate }),
      ...(currentEndDate && { endDate: currentEndDate }),
      ...(currentPaymentMethod && { paymentMode: currentPaymentMethod }),
      ...(currentPaymentReceivedStatus && {
        paymentReceivedStatus: currentPaymentReceivedStatus,
      }),
      ...(currentPaymentPayType && { paymentPayType: currentPaymentPayType }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    console.log("Changing to page:", page);
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const actions: TableAction[] = [
    {
      label: "View Details",
      onClick: (row: any) => {
        navigate(`/cx/payments/view/${row.id}`);
      },
    },
    {
      label: "Download Receipt",
      onClick: (row: any) => {
        handleViewReceipt(row);
      },
    },
    {
      label: "Create Credit Note",
      onClick: (row: any) => {
        handleViewCreditNote(row);
      },
    },
    {
      label: "Transfer Payment",
      onClick: (row: any) => {
        handleViewTransferPayment(row);
      },
      disabled: (row: any) => {
        const type = row.paymentPayTypeDisplay || row.paymentPayType || "";
        const status =
          row.paymentReceivedStatusDisplay || row.paymentReceivedStatus || "";
        const isEligibleType =
          type === "Bill Payment" || type === "Registration";
        const isPosted = status === "Posted";
        const hasAmount = parseFloat(row.amount) > 0;
        return !(isEligibleType && isPosted && hasAmount);
      },
    },
    {
      label: "Refund Payment",
      onClick: (row: any) => {
        handleViewRefundPayment(row);
      },
      disabled: (row: any) => {
        const type = row.paymentPayTypeDisplay || row.paymentPayType || "";
        const status =
          row.paymentReceivedStatusDisplay || row.paymentReceivedStatus || "";
        const isEligibleType =
          type === "Bill Payment" || type === "Registration";
        const isPosted = status === "Posted";
        const hasAmount = parseFloat(row.amount) > 0;
        // const hasRefundableBalance = parseFloat(row.refundableAmount || 0) > 0;
        return !(isEligibleType && isPosted && hasAmount);
      },
    },
  ];

  const handleExport = () => {
    exportCSV();
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    // setCurrentPage(1);

    const sortFieldValue = (direction === "asc" ? "+" : "-") + field;
    const params = new URLSearchParams(searchParams);
    params.set("sort_field", sortFieldValue); // use underscore key
    params.set("page", String(currentPage));
    setSearchParams(params);
  };

  const handleViewPaymentDetails = (payment: PaymentDetail) => {
    setSelectedPayment(payment);
    setIsPaymentDetailOpen(true);
  };

  const handleViewCreditNote = (payment: PaymentDetail) => {
    setSelectedPayment(payment);
    setIsCreditNoteOpen(true);
  };

  const handleViewTransferPayment = (payment: PaymentDetail) => {
    setSelectedPayment(payment);
    setIsTransferPaymentOpen(true);
  };

  const handleViewRefundPayment = (payment: PaymentDetail) => {
    setSelectedPayment(payment);
    setIsRefundPaymentOpen(true);
  };

  const handleViewReceipt = (payment: PaymentDetail) => {
    setSelectedPayment(payment);
    setIsReceiptOpen(true);
  };

  const pagination = data
    ? {
        currentPage,
        totalPages:
          data.total_pages || Math.ceil((data.count || 0) / PAGE_SIZE),
        totalItems: data.count || 0,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, data.count || 0),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Start Date</label>
          <input
            type="date"
            value={pendingFilters.startDate}
            onChange={(e) =>
              handlePendingFilterChange("startDate", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Select start date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">End Date</label>
          <input
            type="date"
            value={pendingFilters.endDate}
            onChange={(e) =>
              handlePendingFilterChange("endDate", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Select end date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Payment Method
          </label>
          <Select
            value={pendingFilters.paymentMethod}
            onValueChange={(value) =>
              handlePendingFilterChange("paymentMethod", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select payment method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              {paymentMethodQuery?.data?.result?.map((method) => (
                <SelectItem key={method.name} value={method.code}>
                  {method.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Payment Status
          </label>
          <Select
            value={pendingFilters.paymentReceivedStatus}
            onValueChange={(value) =>
              handlePendingFilterChange("paymentReceivedStatus", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select payment status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {paymentReceivedStatusChoicesQuery.data?.result?.map((status) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Payment Type</label>
          <Select
            value={pendingFilters.paymentPayType}
            onValueChange={(value) =>
              handlePendingFilterChange("paymentPayType", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select payment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {paymentPayTypeChoicesQuery.data?.result?.map((type) => (
                <SelectItem key={type.key} value={String(type.key)}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters =
    currentStartDate ||
    currentEndDate ||
    currentPaymentMethod !== "all" ||
    currentPaymentReceivedStatus !== "all" ||
    currentPaymentPayType !== "all";

  return (
    <>
      <GenericTable
        data={data?.results || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        // pagination={pagination}
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
          totalItems: data?.count || 0,
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
        }}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        onExport={handleExport}
        exportLoading={isExporting} // Add this line
        filterPanel={FilterPanel}
        emptyMessage="No payments found"
        sortConfig={sortConfig}
        onSort={handleSort}
        emptyIcon={
          <DollarSign className="h-8 w-8 text-muted-foreground mb-2" />
        }
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />
      <PaymentDetailsModal
        isOpen={isPaymentDetailOpen}
        onClose={() => setIsPaymentDetailOpen(false)}
        payment={selectedPayment}
      />
      <CreditNoteDialog
        open={isCreditNoteOpen}
        onClose={() => setIsCreditNoteOpen(false)}
        payment={selectedPayment}
        remoteUtilityId={Number(remoteUtilityId)}
      />
      <TransferPaymentDialog
        isOpen={isTransferPaymentOpen}
        onClose={() => setIsTransferPaymentOpen(false)}
        payment={selectedPayment}
      />
      <RefundPaymentDialog
        isOpen={isRefundPaymentOpen}
        onClose={() => setIsRefundPaymentOpen(false)}
        payment={selectedPayment}
      />
      {selectedPayment?.id && (
        <PaymentReceipt
          isOpen={isReceiptOpen}
          onClose={() => setIsReceiptOpen(false)}
          paymentId={selectedPayment.id}
          formData={selectedPayment}
          autoDownload={true}
        />
      )}
    </>
  );
};
